/**
 * Assets: uvt-base.js
 * -----------------------------------------------------------------------------
 * Shared helpers for the uvt landing page sections, exposed on window.uvt.
 * - initDots: keeps carousel pagination dots in sync with a scrolling rail.
 * - initModals: opens native <dialog> modals from trigger buttons.
 * Several sections load this file, so it only sets itself up once.
 *
 */
(() => {
  if (window.uvt) {
    return;
  }

  const mobileQuery = window.matchMedia('(max-width: 680px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Syncs pagination dots with a horizontally scrolling rail on mobile.
   * Dot taps glide to the card (a jump with reduced motion). The active dot
   * follows the scroll position, read once per animation frame from cached
   * card positions, so scrolling never triggers layout work.
   * @param {HTMLElement} rail - Scrolling container, its children are the cards.
   * @param {HTMLElement[]} dots - One button per card, in the same order.
   */
  function initDots(rail, dots) {
    if (!rail || !dots.length || rail.dataset.uvtDots) {
      return;
    }

    rail.dataset.uvtDots = 'true';
    const cards = Array.from(rail.children);
    let positions = [];
    let active = -1;
    let target = null;
    let frame = 0;
    let settleTimer = 0;

    /**
     * Measures the scroll position that lines each card up with the rail
     * padding. The last dot is the end of the rail.
     */
    function measure() {
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const padding = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
      const railLeft = rail.getBoundingClientRect().left;

      positions = dots.map((dot, index) => {
        const card = cards[index];

        if (index === dots.length - 1) {
          return maxScroll;
        }

        if (!card) {
          return 0;
        }

        const left = card.getBoundingClientRect().left - railLeft + rail.scrollLeft - padding;

        return Math.max(0, Math.min(maxScroll, left));
      });
    }

    /**
     * Marks a dot as current, only touching the DOM when it changes.
     * @param {Number} index - Dot index.
     */
    function setActive(index) {
      if (index === active) {
        return;
      }

      active = index;
      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', String(dotIndex === index));
      });
    }

    /**
     * Marks the dot closest to the current scroll position as current. While a
     * dot tap is gliding, its dot stays current so the dots don't flicker.
     */
    function update() {
      if (target !== null) {
        setActive(target);
        return;
      }

      const scrollLeft = rail.scrollLeft;
      let closest = 0;
      let distance = Infinity;

      positions.forEach((left, index) => {
        const dotDistance = Math.abs(scrollLeft - left);

        if (dotDistance < distance) {
          distance = dotDistance;
          closest = index;
        }
      });

      setActive(closest);
    }

    /**
     * Ends a dot tap glide and syncs the dots with where the rail stopped.
     */
    function settle() {
      target = null;
      update();
    }

    /**
     * Schedules one update per animation frame while the rail scrolls.
     * scrollend isn't supported everywhere, so a short pause also settles.
     */
    function onScroll() {
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          update();
        });
      }

      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 150);
    }

    /**
     * A new touch or wheel gesture takes over from a dot tap glide.
     */
    function release() {
      target = null;
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (!mobileQuery.matches) {
          return;
        }

        measure();
        target = index;
        setActive(index);
        rail.scrollTo({
          behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
          left: positions[index],
        });

        // Already in place, so no scroll event will settle it
        if (Math.abs(rail.scrollLeft - positions[index]) < 1) {
          settle();
        }
      });
    });

    rail.addEventListener('scroll', onScroll, { passive: true });
    rail.addEventListener('scrollend', settle, { passive: true });
    rail.addEventListener('pointerdown', release, { passive: true });
    rail.addEventListener('wheel', release, { passive: true });

    new ResizeObserver(() => {
      measure();
      update();
    }).observe(rail);

    mobileQuery.addEventListener('change', () => {
      if (!mobileQuery.matches) {
        rail.scrollLeft = 0;
      }

      measure();
      update();
    });

    measure();
    update();
  }

  /**
   * Opens <dialog> modals inside a section from [data-uvt-dialog] triggers.
   * The trigger's data-uvt-dialog value is the dialog's id.
   * Focus returns to the trigger on close, clicking the backdrop closes it and
 * page scroll is locked while a modal is open.
   * @param {HTMLElement} root - Section element containing triggers and dialogs.
   */
  function initModals(root) {
    if (!root || root.dataset.uvtModals) {
      return;
    }

    root.dataset.uvtModals = 'true';
    let opener = null;

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-uvt-dialog]');

      if (!trigger || !root.contains(trigger)) {
        return;
      }

      const dialog = document.getElementById(trigger.dataset.uvtDialog);

      if (!(dialog instanceof HTMLDialogElement)) {
        return;
      }

      opener = trigger;
      dialog.showModal();
      document.documentElement.classList.add('uvt-scroll-locked');
    });

    root.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog.uvt-modal[open]')) {
          document.documentElement.classList.remove('uvt-scroll-locked');
        }

        if (opener && opener.isConnected) {
          opener.focus();
        }

        opener = null;
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          dialog.close();
        }
      });
    });
  }

  window.uvt = {
    initDots,
    initModals,
  };
})();
